import {
  useCreatePlayerPackMutation,
  useGetProjectionsForPlayerPacksQuery
} from '@bo-services/playerPackApi';
import type { GamePlayerProjectionsRequest } from '@library/types/projection';
import type { PlayerPackProjection } from '@library/types/playerPacks/playerPacks';
import { playerPacks } from '@bo-test/mock/data/playerPacks';
import { useGames } from '@bo-hooks/useGames';
import { useMemo } from 'react';

export const usePlayerPacks = ({
  ...projectionsQueryParams
}: GamePlayerProjectionsRequest) => {
  const { leagueId, fromDate, toDate } = projectionsQueryParams;
  const id = leagueId?.[0];
  const {
    data: response,
    isError: isProjectionsError,
    isLoading: isProjectionsLoading,
    isFetching: isProjectionsFetching
  } = useGetProjectionsForPlayerPacksQuery(
    { ...projectionsQueryParams },
    { skip: !id }
  );

  const { games } = useGames({
    leagueId: leagueId?.[0],
    fromDate,
    toDate
  });

  const projectionsForPicks = useMemo(
    () =>
      response?.data?.map((projection) => {
        const gameToDisplay = games.find(
          (g) => g.game_id === projection.attributes.game_id
        );

        return {
          playerName: response.included.find(
            (player) =>
              player.id === projection.relationships.new_player.data?.id
          )?.attributes.name,
          playerImageUrl: response.included.find(
            (player) =>
              player.id === projection.relationships.new_player.data?.id
          )?.attributes.image_url,
          projectionId: projection.id,
          projectionValue: projection.attributes.line_score,
          gameId: projection.attributes.game_id,
          gameDisplay: gameToDisplay
            ? `${gameToDisplay.away_team_name} vs ${gameToDisplay.home_team_name} • ${new Date(gameToDisplay.date_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
            : '',

          gameTime: games.find(
            (game) => game.game_id === projection.attributes.game_id
          )?.date_time,
          statType: projection.attributes.stat_type,
          oddsType: projection.attributes.odds_type
        };
      }) as Array<PlayerPackProjection>,
    [response, games]
  );

  const [
    createPlayerPack,
    {
      isLoading: isCreatingPlayerPack,
      error: createPlayerPackError,
      isSuccess: createPlayerPackSuccess
    }
  ] = useCreatePlayerPackMutation();

  return {
    createPlayerPack,
    createPlayerPackError,
    createPlayerPackSuccess,
    currentPage: 1,
    currentProjectionsPage: response?.meta?.current_page,
    games,
    isCreatingPlayerPack,
    isProjectionsError,
    isLoadingProjections: isProjectionsLoading || isProjectionsFetching,
    playerPacks,
    projectionsForPicks,
    projectionsTotalPages: response?.meta?.total_pages
  };
};
